// Contextual DUMMY data for previewing a section in the "Add section" picker.
// Walks a block's Zod schema (like starter.ts) but fills leaves with realistic
// placeholder content — headings, copy, and image placeholders — so the user
// sees what a section looks like before adding it. Only used for the preview;
// the actual added block still starts from starterProps (empty, to fill in).

// A self-contained SVG placeholder (data URI) — always renders in the preview
// iframe, no network or CSP concerns.
const IMG = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="800" height="500">' +
    '<rect width="100%" height="100%" fill="#e2e8f0"/>' +
    '<g fill="#94a3b8"><circle cx="300" cy="185" r="46"/>' +
    '<path d="M170 380l150-150 110 110 95-75 135 115v20H170z"/></g></svg>',
)}`;

const isImageKey = (k: string) => /image|img|photo|avatar|logo|icon|thumb|banner|bg|picture|media/i.test(k);
const isLinkKey = (k: string) => /url|href|link|slug|path/i.test(k);

const COPY = 'Protect every user, app, and device with unified identity — from onboarding to offboarding, without the busywork.';
const TITLES = ['Single sign-on', 'Multi-factor authentication', 'Lifecycle automation', 'Access governance', 'Passwordless login', 'Privileged access'];
const NAMES = ['Alex Morgan', 'Jordan Lee', 'Priya Sharma', 'Sam Rivera'];

function sampleString(key: string): string {
  const k = key.toLowerCase();
  if (isImageKey(k)) return IMG;
  if (k.includes('linktext') || k.includes('label') || k.includes('cta') || k.includes('button')) return 'Learn more';
  if (isLinkKey(k)) return '#';
  if (k.includes('eyebrow') || k.includes('kicker') || k.includes('badge') || k.includes('tag')) return 'WHY CYMMETRI';
  if (k.includes('heading') || k.includes('title') || k.includes('headline')) return 'Identity that scales with you';
  if (k.includes('description') || k.includes('review') || k.includes('quote') || k.includes('excerpt') ||
      k.includes('body') || k.includes('content') || k.includes('subtitle') || k.includes('summary') || k === 'text') return COPY;
  if (k.includes('name')) return 'Alex Morgan';
  if (k.includes('designation') || k.includes('role') || k.includes('position') || k.includes('author') || k.includes('company')) return 'Head of Security, Acme';
  if (k.includes('date')) return 'Jan 2026';
  if (k.includes('minute') || k.includes('read') || k.includes('time')) return '5 min read';
  if (k.includes('stat') || k.includes('metric') || k.includes('value') || k.includes('number') || k.includes('percent') || k.includes('count')) return '98%';
  if (k.includes('tab')) return 'Overview';
  return 'Sample text';
}

function build(schema: any, key = ''): any {
  const def = schema?._def;
  if (!def) return undefined;
  switch (def.typeName) {
    case 'ZodOptional':
    case 'ZodNullable':
      return build(def.innerType, key);
    case 'ZodDefault':
      try { return def.defaultValue(); } catch { return build(def.innerType, key); }
    case 'ZodEffects':
      return build(def.schema, key);
    case 'ZodObject': {
      const shape = typeof schema.shape === 'function' ? schema.shape() : schema.shape;
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(shape)) {
        const v = build(shape[k], k);
        if (v !== undefined) out[k] = v;
      }
      return out;
    }
    case 'ZodArray': {
      const k = key.toLowerCase();
      if (k === 'headinglines') return ['Secure every identity,', 'everywhere it matters'];
      if (isImageKey(k)) return [IMG, IMG, IMG, IMG, IMG];
      const item = build(def.type, singular(key));
      if (item === undefined) return [];
      const n = 3;
      return Array.from({ length: n }, (_, i) => vary(item, i));
    }
    case 'ZodString':
      return sampleString(key);
    case 'ZodNumber':
      return 3;
    case 'ZodBoolean':
      return true;
    case 'ZodEnum':
      return def.values?.[0] ?? '';
    case 'ZodUnion':
      return build(def.options?.[0], key);
    default:
      return undefined;
  }
}

const singular = (key: string) => key.replace(/s$/, '');

// Give list items realistic variety (distinct titles/names per row).
function vary(item: any, i: number): any {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
  const out = { ...item };
  for (const k of Object.keys(out)) {
    const lk = k.toLowerCase();
    if (typeof out[k] !== 'string') continue;
    if (lk.includes('title') || lk.includes('heading')) out[k] = TITLES[i % TITLES.length];
    else if (lk.includes('name') && !lk.includes('image')) out[k] = NAMES[i % NAMES.length];
  }
  return out;
}

/** Realistic dummy props for previewing a block, derived from its schema. */
export function sampleProps(schema: unknown): Record<string, unknown> {
  const v = build(schema);
  return v && typeof v === 'object' ? v : {};
}
